/**
 * PrivacyPage — the plain-English privacy statement reachable from the landing-page "Privacy"
 * nav link. Mirrors SecurityPage's confidence-building pattern. It reflects what is actually
 * built (AU-only storage, field-level encryption, export/delete endpoints, advice-record
 * retention) — never promise here what the system doesn't do. The formal Privacy Policy and
 * the collection notice open from this page in the same reader the sign-up checkbox uses.
 */
import React, { useState } from 'react';
import LegalDocModal, { type LegalDoc } from '../components/legal/LegalDocModal.tsx';
import { PRIVACY_POLICY, COLLECTION_NOTICE } from '../constants/legal/index.ts';
import type { LucideIcon } from 'lucide-react';
import {
  ScrollText, ClipboardList, EyeOff, MapPin, UserCheck, Timer, Siren,
  ArrowLeft, CheckCircle2, Mail, ShieldCheck,
} from 'lucide-react';

interface PromiseCard {
  icon: LucideIcon;
  title: string;
  body: string;
}

const PROMISES: PromiseCard[] = [
  {
    icon: ClipboardList,
    title: 'We collect only what we need',
    body: 'Your email to run your account, and the details you enter about your situation and finances — because that’s what your plan is built from. Card payments are handled by Stripe; your card number never touches our servers.',
  },
  {
    icon: EyeOff,
    title: 'Never sold, never advertising',
    body: 'Your information is used for one thing: producing your plan and advice. We don’t sell it, share it with marketers, or use it to profile you for ads. Full stop.',
  },
  {
    icon: MapPin,
    title: 'Stored in Australia',
    body: 'Your personal and financial details are stored in Australia, protected by Australian privacy law, and encrypted so they’re unreadable without keys we keep separately.',
  },
  {
    icon: UserCheck,
    title: 'Shared only as you direct',
    body: 'If a professional adviser invited you, they see the file they’re helping you with — that’s the service. Beyond that, your information goes only to the few specialist providers who help us run AgedCareWise — each described in our Privacy Policy — or where the law requires it.',
  },
  {
    icon: Timer,
    title: 'Kept only as long as needed',
    body: 'If your access ends, your information is locked away — not deleted — so you can pick up where you left off if you return. Advice records must be kept for 7 years by law; we’re upfront about that.',
  },
  {
    icon: Siren,
    title: 'If something ever goes wrong',
    body: 'In the unlikely event of a data breach that could cause you harm, we’ll tell you and the privacy regulator promptly, plainly, and with clear steps — as the Notifiable Data Breaches scheme requires.',
  },
];

const RIGHTS: string[] = [
  'Download a copy of your account and planning information, any time, from your account settings — and ask us for anything else we hold about you.',
  'Ask us to correct anything that’s wrong.',
  'Ask us to delete your information when you no longer need the service (advice records the law requires us to keep are the one exception — we’ll tell you exactly what and why).',
  'Complain — first to us, and if you’re not satisfied, to the Office of the Australian Information Commissioner (OAIC).',
];

export interface PrivacyPageProps {
  onBack: () => void;
  onOpenSecurity?: () => void;
}

const PrivacyPage = ({ onBack, onOpenSecurity }: PrivacyPageProps) => {
  const [viewDoc, setViewDoc] = useState<LegalDoc | null>(null);
  return (
  <div className="min-h-screen bg-slate-50">
    {/* Top bar */}
    <div className="sticky top-0 z-10 border-b border-gray-200 bg-white/90 backdrop-blur">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-gray-600 transition hover:bg-gray-100 hover:text-gray-900"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <div className="text-lg font-bold">
          <span className="text-slate-900">AgedCare</span><span className="text-[#17b8e0]">Wise</span>
        </div>
      </div>
    </div>

    {/* Hero */}
    <div className="relative overflow-hidden bg-gradient-to-br from-[#1a6fd4] to-[#17b8e0] text-white">
      <div className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
      <div className="pointer-events-none absolute -left-10 bottom-0 h-48 w-48 rounded-full bg-white/5 blur-2xl" />
      <div className="relative mx-auto max-w-5xl px-5 py-14 text-center">
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/25 backdrop-blur">
          <ScrollText className="h-8 w-8" />
        </div>
        <h1 className="text-3xl font-bold sm:text-4xl">Your privacy</h1>
        <p className="mx-auto mt-3 max-w-2xl text-base text-white/90 sm:text-lg">
          Planning for aged care means sharing personal things. Here’s exactly what we collect,
          what we do with it, and the control you keep — no legal maze.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <button
            type="button"
            onClick={() => setViewDoc(PRIVACY_POLICY)}
            className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#1a6fd4] shadow-md transition hover:shadow-lg"
          >
            <ScrollText size={16} /> Read the full Privacy Policy
          </button>
          <button
            type="button"
            onClick={() => setViewDoc(COLLECTION_NOTICE)}
            className="inline-flex items-center gap-2 rounded-full bg-white/15 px-5 py-2.5 text-sm font-semibold text-white ring-1 ring-white/30 transition hover:bg-white/25"
          >
            <ClipboardList size={16} /> How we handle your information
          </button>
        </div>
      </div>
    </div>

    <div className="mx-auto max-w-5xl px-5 py-12 space-y-12">
      {/* Promise cards */}
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {PROMISES.map(({ icon: Icon, title, body }) => (
          <div key={title} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition hover:shadow-md">
            <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-[#1a6fd4]">
              <Icon size={22} />
            </div>
            <h3 className="text-base font-bold text-slate-900">{title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-gray-600">{body}</p>
          </div>
        ))}
      </div>

      {/* Your rights */}
      <div className="rounded-2xl border border-gray-200 bg-white p-7 shadow-sm sm:p-9">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-[#1a6fd4]">
            <UserCheck size={24} />
          </div>
          <div className="w-full">
            <h2 className="text-xl font-bold text-slate-900">You stay in control</h2>
            <p className="mt-2 text-sm leading-relaxed text-gray-600 sm:text-base">
              Under Australia’s <strong>Privacy Act</strong> — and because it’s simply right — your
              information belongs to you. At any time you can:
            </p>
            <ul className="mt-4 space-y-2.5">
              {RIGHTS.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-gray-700">
                  <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-emerald-500" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* One honest note (APP 8) */}
      <div className="rounded-2xl border border-gray-200 bg-white p-7 shadow-sm sm:p-9">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-[#1a6fd4]">
            <Mail size={24} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">One honest footnote</h2>
            <p className="mt-2 text-sm leading-relaxed text-gray-600 sm:text-base">
              When we email you (for example, a sign-in code), the email itself is delivered by a trusted
              provider whose servers may route it outside Australia — the same way most email works. Only
              your email address and the message travel that way. Your financial details and your plan
              are stored only in Australia, and our formal Privacy Policy lists every service provider we
              use and where they operate.
            </p>
          </div>
        </div>
      </div>

      {/* How we protect it → Security page */}
      <div className="rounded-2xl border border-gray-200 bg-white p-7 shadow-sm sm:p-9">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-[#1a6fd4]">
            <ShieldCheck size={24} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">How we protect it</h2>
            <p className="mt-2 text-sm leading-relaxed text-gray-600 sm:text-base">
              Bank-grade encryption, strict walls between every client’s information, two-factor login,
              and independent security testing — the full picture is on our Security page.
            </p>
            {onOpenSecurity && (
              <button
                type="button"
                onClick={onOpenSecurity}
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#1a6fd4] to-[#17b8e0] px-5 py-2.5 text-sm font-semibold text-white shadow-md transition hover:shadow-lg"
              >
                <ShieldCheck size={16} /> See how we keep it safe
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Reassurance footer */}
      <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-700 p-7 text-center text-white sm:p-9">
        <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/20">
          <ScrollText size={20} />
        </div>
        <h2 className="text-lg font-bold sm:text-xl">Questions about your privacy?</h2>
        <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-white/80">
          Write to us at <span className="font-semibold text-white">privacy@agedcarewise.com.au</span> and
          a person — not a bot — will get back to you.
        </p>
      </div>
    </div>
    {viewDoc && <LegalDocModal doc={viewDoc} onClose={() => setViewDoc(null)} />}
  </div>
  );
};

export default PrivacyPage;
